import React from 'react';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import HelpIcon from '@material-ui/icons/HelpOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import DefaultDialog from 'ui/dialog/DefaultDialog';
import DialogButtons from 'ui/dialog/Buttons';
import ClipboardJS from 'clipboard';

import StatusIcon from 'ui/StatusIcon';

import { withModalStyles, withPolicyStyles } from './styles';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
    button: {
        marginRight: theme.spacing(2),

        '&:last-child': {
            marginRight: 0,
        },
    },
}));

const DisabledBackupPolicyDialogView = ({ open, onClose, onDisable }) => {
    const classes = useStyles();

    return (
        <div>
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Disabling this policy will immediately delete all
                        backups and delete your backup keys. This process cannot
                        be undone. Prior to disabling this policy, download any
                        backup files that you need to keep.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={classes.root}>
                    <Button
                        className={classes.button}
                        onClick={onClose}
                        color="secondary"
                    >
                        CANCEL
                    </Button>
                    <Button
                        className={classes.button}
                        variant="contained"
                        onClick={onDisable}
                        color="secondary"
                    >
                        Disable Policy
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const DisabledBackupPolicyDialog = withModalStyles(
    DisabledBackupPolicyDialogView
);

class PrivateKeyDialog extends React.Component {
    constructor(props) {
        super(props);
        this.privateKeyRef = React.createRef();
        this.copyButton = React.createRef();
    }

    componentDidUpdate() {
        if (!this.copyButton.current) {
            return;
        }

        this.clipboard = new ClipboardJS(this.copyButton.current, {
            target: () => this.privateKeyRef.current,
        });
    }

    componentWillUnmount() {
        if (this.clipboard) {
            this.clipboard.destroy();
        }
    }

    getPrivateKey(policy) {
        let privateKey = policy.value.interpreted;
        try {
            privateKey = atob(privateKey);
            if (!privateKey.includes('-----BEGIN PRIVATE KEY-----')) {
                return false;
            }
            privateKey = privateKey.replace(
                '-----BEGIN PRIVATE KEY-----',
                '-----BEGIN PRIVATE KEY-----<br/>'
            );
            privateKey = privateKey.replace(
                '-----END PRIVATE KEY-----',
                '<br/>-----END PRIVATE KEY-----'
            );

            return privateKey;
        } catch (e) {
            return false;
        }
    }

    render() {
        const { classes, policy, open, onClose, pb6Enabled } = this.props;
        const privateKey = this.getPrivateKey(policy);

        return (
            <div>
                <DefaultDialog
                    open={open}
                    onClose={onClose}
                    classes={{
                        dialog: classes.dialog,
                        dialogFixed: !pb6Enabled
                            ? classes.dialogPrivateKey
                            : classes.dialogFixed,
                    }}
                >
                    {!pb6Enabled ? (
                        <>
                            <DialogTitle id="form-dialog-title">
                                Save your private key
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Backups of team items are now enabled for
                                    your team. Save the private key listed
                                    below, it will not be shown again. You will
                                    need this private key to access the backup
                                    data.
                                </DialogContentText>
                                <Button
                                    ref={this.copyButton}
                                    color="primary"
                                    style={{ float: 'right' }}
                                >
                                    <FileCopyIcon />
                                    Copy
                                </Button>
                                <DialogContentText>
                                    {!open || !privateKey ? (
                                        <CircularProgress />
                                    ) : (
                                        <div
                                            className={classes.privateKey}
                                            dangerouslySetInnerHTML={{
                                                __html: privateKey,
                                            }}
                                            ref={this.privateKeyRef}
                                            style={{
                                                overflowY: 'scroll',
                                                maxHeight: '300px',
                                            }}
                                        />
                                    )}
                                </DialogContentText>
                            </DialogContent>
                        </>
                    ) : (
                        <>
                            <DialogTitle id="form-dialog-title">
                                Keep your private key safe.
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Backups of team items are now enabled for
                                    your team. Members of the recovery group
                                    will have access to the recovery private
                                    key.
                                </DialogContentText>
                                <DialogContentText>
                                    IMPORTANT: Admins are automatically members
                                    of the recovery group. For changes to
                                    Recovery group members to take effect, at
                                    least one member with a recovery key must
                                    log in or be active in the Web App after the
                                    changes are made.
                                </DialogContentText>
                            </DialogContent>
                        </>
                    )}
                    <DialogActions>
                        <DialogButtons>
                            <DialogButtons.Primary
                                variant="contained"
                                onClick={onClose}
                                label="OK"
                            />
                        </DialogButtons>
                    </DialogActions>
                </DefaultDialog>
            </div>
        );
    }
}

PrivateKeyDialog = withModalStyles(PrivateKeyDialog);

class BackupAllItemPolicy extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false,
            openPrivateKeyDialog: false,
            openDisabledConfirmationDialog: false,
        };
    }

    handleExpandeChange = (event, expanded) => {
        this.setState({ expanded });
    };

    handleEnableChange = policy => () => {
        this.setState((state, props) => {
            if (props.policy.enabled) {
                //disable
                return { openDisabledConfirmationDialog: true };
            } else {
                //enable
                let data = {
                    uuid: policy.uuid,
                    enabled: true,
                };
                this.props.onPolicyEnableChange(data);
                return { openPrivateKeyDialog: true };
            }
        });
    };

    handleCloseDialog = () => {
        this.setState({
            openPrivateKeyDialog: false,
            openDisabledConfirmationDialog: false,
        });
    };

    handleDisablePolicy = () => {
        let data = {
            uuid: this.props.policy.uuid,
            enabled: false,
        };
        this.props.onPolicyEnableChange(data);
        this.handleCloseDialog();
    };

    renderDescription = policy => {
        return policy.enabled ? 'Enabled' : 'Disabled';
    };

    render() {
        const { classes, policy, editable, pb6Enabled } = this.props;
        return (
            <ExpansionPanel onChange={this.handleExpandeChange}>
                <ExpansionPanelSummary
                    expandIcon={<ExpandMoreIcon />}
                    classes={{
                        expandIcon: classes.expandIcon,
                        content: classes.content,
                    }}
                >
                    <div className={classes.columnName}>
                        <Typography className={classes.heading}>
                            {policy.policy.name}
                        </Typography>
                        <Tooltip
                            placement="right"
                            title={policy.policy.details}
                            classes={{ tooltip: classes.tooltip }}
                        >
                            <HelpIcon className={classes.help} />
                        </Tooltip>
                    </div>
                    <div className={classes.columnDetail}>
                        {this.state.expanded ? (
                            <Typography className={classes.secondaryHeading}>
                                {policy.policy.description}
                            </Typography>
                        ) : (
                            <div className={classes.info}>
                                <StatusIcon active={policy.enabled} />
                                <Typography
                                    className={classes.secondaryHeading}
                                >
                                    {this.renderDescription(policy)}
                                </Typography>
                            </div>
                        )}
                    </div>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <div className={classes.columnName} />
                    <div className={classes.columnDetail}>
                        <Switch
                            checked={policy.enabled}
                            onChange={this.handleEnableChange(policy)}
                            classes={{ root: classes.switch }}
                            disabled={!editable}
                        />
                        <PrivateKeyDialog
                            open={this.state.openPrivateKeyDialog}
                            policy={policy}
                            onClose={this.handleCloseDialog}
                            pb6Enabled={pb6Enabled}
                        />
                        <DisabledBackupPolicyDialog
                            open={this.state.openDisabledConfirmationDialog}
                            onClose={this.handleCloseDialog}
                            onDisable={this.handleDisablePolicy}
                        />
                    </div>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    }
}

export default withPolicyStyles(BackupAllItemPolicy);
